/* src/components/pages/Dashboard.jsx - Modificado el 26-03-2026 */
/* --------- Dashboard premium (Stripe / Notion UI) ------------ */

import React, {
  useEffect,
  useState,
  useCallback
} from "react";

import { useNavigate } from "react-router-dom";

import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  Skeleton,
} from "@mui/material";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

import { useAuth } from "../../context/AuthContext";
import { useFeedback } from "../../context/FeedbackContext";
import api from "../../services/api";

const meses = [
  "", "Ene","Feb","Mar","Abr","May","Jun",
  "Jul","Ago","Sep","Oct","Nov","Dic"
];

/* ===== Helpers ===== */

const formatMoneyShort = (value) => {
  if (value >= 1_000_000)
    return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000)
    return `${(value / 1_000).toFixed(1)}K`;
  return value;
};

/* ===== 🔥 TREND REAL VENTAS ===== */
const buildTrend = (series) => {
  if (!series?.neto) return [];

  const map = {};

  series.neto.forEach((r) => {
    map[r.mes] = Number(r.total) || 0;
  });

  const result = [];

  for (let m = 1; m <= 12; m++) {
    result.push({
      mes: m,
      v: map[m] || 0,
    });
  }

  return result;
};


/* ===== ALERTA VENTAS ===== */
const getVentasAlert = (trend = []) => {
  if (!trend.length) return null;

  const last = trend.slice(-3);

  if (last.length < 3) return null;

  const falling =
    last[2].v < last[1].v &&
    last[1].v < last[0].v;

  if (!falling) return null;

  return {
    tipo: "warning",
    titulo: "Ventas en descenso",
    descripcion: "Tus ventas han caído durante los últimos 2 meses",
  };
};

/* ===== PROYECCIÓN SIMPLE ===== */
const getProyeccion = (trend = []) => {
  if (!trend.length) return 0;

  const currentMonth = new Date().getMonth() + 1;

  const actual = trend.find(t => t.mes === currentMonth)?.v || 0;

  // proyección simple (lineal)
  const day = new Date().getDate();
  const daysInMonth = new Date(
    new Date().getFullYear(),
    currentMonth,
    0
  ).getDate();

  const proyectado = (actual / day) * daysInMonth;

  return Math.round(proyectado);
};

/* ===== Salud Financiera ===== */
const getHealthScore = (kpis, trend = []) => {
  if (!kpis) return { score: 0, label: "Sin datos", color: "#999" };

  let score = 50;

  if (kpis.varVentas > 0) score += 20;
  else score -= 20;

  if (kpis.varCompras > 20) score -= 10;
  if (kpis.pendientes > 0) score -= 20;
  if (kpis.varCesiones > 0) score += 10;

  // 🔥 NUEVO: tendencia real
  const last = trend.slice(-3);
  const falling = last.every((v, i, arr) =>
    i === 0 || v.v <= arr[i - 1].v
  );

  if (falling) score -= 15;

  score = Math.max(0, Math.min(100, score));

  if (score >= 75) return { score, label: "Salud excelente", color: "#22c55e" };
  if (score >= 50) return { score, label: "Salud estable", color: "#eab308" };
  return { score, label: "Riesgo financiero", color: "#ef4444" };
};



/* ===== KPI CARD ===== */
const KpiCard = ({ title, value, variation = 0, color, trend = [], extra }) => {
  const theme = useTheme();
  const ui = theme.custom.ui;

  const isPositive = variation >= 0;

  const safe = Number(value) || 0; // 🔥 FIX importante

  return (
    <Card
      sx={{
        flex: "1 1 220px",
        minWidth: 220,
        position: "relative",

        background: ui.glass?.background || ui.cardBackground,
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",

        border: `1px solid ${ui.glass?.border || ui.borderSubtle}`,
        borderRadius: 3,

        transition: "all 0.2s ease",

        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${color}, transparent)`,
        },

        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: ui.glass?.shadowHover || ui.surfaceShadow,
        },
      }}
    >
      <CardContent>

        {/* TITLE */}
        <Typography sx={{ fontSize: 11, color: ui.textSecondary }}>
          {title}
        </Typography>

        {/* VALUE */}
        <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
          $<AnimatedNumber value={safe} />
        </Typography>

        {extra && (
          <Typography
            sx={{
              fontSize: 11,
              color: ui.textSecondary,
              mt: 0.3,
            }}
          >
            Mes actual: ${formatMoneyShort(extra.actual)} vs ${formatMoneyShort(extra.anterior)} año anterior
          </Typography>
        )}

        {/* VARIATION */}
        <Typography
          sx={{
            mt: 0.5,
            fontSize: 12,
            color: isPositive ? ui.status.success.bg : ui.status.error.text,
            fontWeight: 600,
          }}
        >
          {isPositive ? "▲" : "▼"} {Math.abs(variation)}%
        </Typography>

        {/* 🔥 Grafico interno SPARKLINE (AQUÍ VA) */}
        <Box sx={{ mt: 1 }}>
          <ResponsiveContainer width="100%" height={30}>
            <LineChart data={trend}
            >
              <Line
                type="monotone"
                dataKey="v"
                stroke={
                  trend.length > 1 && trend[trend.length - 1]?.v > trend[trend.length - 2]?.v
                    ? ui.status.success.bg
                    : ui.status.error.text
                }
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>

      </CardContent>
    </Card>
  );
};


/* ===== CHART CARD ===== */

const ChartCard = ({ title, data, lines }) => {
  const theme = useTheme();
  const ui = theme.custom.ui;

  return (
    <Card
      sx={{
        flex: {
          xs: "1 1 100%",
          md: "1 1 calc(50% - 16px)",
        },

        background: ui.glass?.background || ui.cardBackground,
        backdropFilter: "blur(12px)",

        border: `1px solid ${ui.glass?.border || ui.borderSubtle}`,
        borderRadius: 3,

        transition: "all 0.2s ease",

        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: ui.glass?.hoverShadow,
        },
      }}
    >
      <CardContent>
        <Typography sx={ui.cardTitleAccent}>{title}</Typography>

        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid stroke={ui.chartGrid} vertical={false} />

            <XAxis
              dataKey="mes"
              tickFormatter={(m) => meses[m]}
              tick={{ fill: ui.chartTick, fontSize: 11 }}
            />

            <YAxis
              tick={{ fill: ui.chartTick, fontSize: 11 }}
              tickFormatter={(v) => formatMoneyShort(v)}
            />

            <Tooltip content={<CustomTooltipDashboard />} />

            {lines.map((l) => (
              <Line
                key={l.key}
                type="monotone"
                dataKey={l.key}
                stroke={l.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

/* ===== ALERT ===== */

const AlertCard = ({ alert }) => {
  const theme = useTheme();
  const ui = theme.custom.ui;

  const colorMap = {
    error: ui.error,
    warning: ui.warning,
    info: ui.info,
  };

  const color = colorMap[alert.tipo] || ui.primary;

  return (
    <Card
      sx={{
        background: ui.glass?.background || ui.cardBackground,
        backdropFilter: "blur(10px)",

        border: `1px solid ${ui.glass?.border || ui.borderSubtle}`,
        borderLeft: `3px solid ${color}`,

        transition: "all 0.2s ease",

        "&:hover": {
          transform: "translateX(6px)",
          boxShadow: ui.glass?.hoverShadow,
        },
      }}
    >
      <CardContent>
        <Typography fontWeight={600}>
          {alert.titulo}
        </Typography>

        <Typography sx={{ fontSize: 12, color: ui.textSecondary }}>
          {alert.descripcion}
        </Typography>
      </CardContent>
    </Card>
  );
};

/* ===== TOOLTIP ===== */
const CustomTooltipDashboard = ({ active, payload, label }) => {
  const theme = useTheme();
  const ui = theme.custom.ui;

  if (!active || !payload?.length) return null;

  return (
    <Box
      sx={{
        background: ui.tooltipBg,
        border: `1px solid ${ui.glass.border}`,
        p: 1.5,
        borderRadius: 2,
        backdropFilter: "blur(10px)",
        boxShadow: ui.glass.shadow,
      }}
    >
      <Typography sx={{ fontSize: 11, color: ui.textSecondary }}>
        {meses[label]}
      </Typography>

      {payload.map((p, i) => (
        <Typography key={i} sx={{ fontSize: 12, fontWeight: 600 }}>
          {p.name}: ${formatMoneyShort(p.value)}
        </Typography>
      ))}
    </Box>
  );
};


/* ===== ANIMATED NUMBER ===== */

const AnimatedNumber = ({ value = 0, duration = 800 }) => {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    let start = 0;
    const safeValue = Number(value) || 0;

    if (safeValue === 0) {
      setDisplay(0);
      return;
    }

    const increment = safeValue / (duration / 16);

    const step = () => {
      start += increment;

      if (start >= safeValue) {
        setDisplay(safeValue);
        return;
      }

      setDisplay(Math.floor(start));
      requestAnimationFrame(step);
    };

    step();
  }, [value, duration]);

  return display.toLocaleString("es-CL");
};


const HealthCard = ({ kpis, trend = [], data = [] }) => {
  const theme = useTheme();
  const ui = theme.custom.ui;

  const health = getHealthScore(kpis, trend);

  /* 🔥 flujo real (ventas - compras) */
  const ultimoFlujo =
    [...(data || [])]
      .reverse()
      .find((m) => m?.flujo !== 0)?.flujo || 0;

  return (
    <Card
      sx={{
        mt: 2,
        p: 2,
        background: ui.glass?.background || ui.surfaceBg,
        border: `1px solid ${ui.glass?.border || ui.borderSubtle}`,
        backdropFilter: "blur(12px)",
        borderRadius: 3,
        boxShadow: ui.glass?.shadow || ui.surfaceShadow, // 🔥 FIX
      }}
    >
      <Typography sx={{ fontSize: 13, mb: 1 }}>
        Salud financiera
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>

        {/* SCORE */}
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 14,
            background: `${health.color}22`,
            color: health.color,
            border: `2px solid ${health.color}`,
          }}
        >
          {health.score}
        </Box>

        {/* TEXTO */}
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={600}>
            {health.label}
          </Typography>

          <Typography sx={{ fontSize: 12, color: ui.textSecondary }}>
            Basado en ventas, compras y flujo
          </Typography>

          {/* 🔥 FLUJO BIEN POSICIONADO */}
          <Typography
            sx={{
              fontSize: 11,
              color: ui.textSecondary,
              mt: 0.5,
            }}
          >
            Flujo último mes: ${formatMoneyShort(ultimoFlujo)}
          </Typography>
        </Box>

      </Box>
    </Card>
  );
};



/* ===== MAIN ===== */

const Dashboard = ({ modo }) => {
  const { user } = useAuth();
  const { showFeedback } = useFeedback();
  const theme = useTheme();
  const ui = theme.custom.ui;
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);

    try {
      const res = await api.post("/dashboard/getResumenCliente", {
        RutCli: user.usuario,
        Anoaconsultar: new Date().getFullYear(),
      });

      if (res.data?.success) {
        setData(res.data);
      }
    } catch {
      showFeedback({
        message: "No fue posible cargar el dashboard",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [user.usuario, showFeedback]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading || !data) {
    return <Skeleton variant="rectangular" height={400} />;
  }

  const { kpis, graficos, alertas } = data;

  const resumenCesiones = data?.resumenCesiones || {};

  // 🔥 tendencia real ventas
  const ventasTrend = buildTrend(graficos?.ventas);

  // 🔥 tendencia real compras
  const comprasTrend = buildTrend(graficos?.compras);

  /* Dataset grafico compras y ventas año actual */
  const enrichedData = (graficos?.ventasVsCompras || []).map((r) => {
    const ventas = Number(r.ventas) || 0;
    const compras = Number(r.compras) || 0;

    const flujo = ventas - compras;

    const margen = ventas > 0
      ? Number(((flujo / ventas) * 100).toFixed(1))
      : 0;

    const ratio = ventas > 0
      ? Number(((compras / ventas) * 100).toFixed(1))
      : 0;

    return {
      ...r,
      ventas,
      compras,
      flujo,
      margen,
      ratio,
    };
  });

  /* Dataset grafico compras y ventas año anterior */
  const enrichedDataAnterior = (() => {
    const map = {};

    const ensureMes = (mes) => {
      if (!map[mes]) {
        map[mes] = { mes, ventas: 0, compras: 0 };
      }
    };

    // ventas
    graficos?.ventasAnterior?.neto?.forEach((r) => {
      ensureMes(r.mes);
      map[r.mes].ventas += Number(r.total) || 0;
    });

    // compras
    graficos?.comprasAnterior?.neto?.forEach((r) => {
      ensureMes(r.mes);
      map[r.mes].compras += Number(r.total) || 0;
    });

    // 🔥 CLAVE: forzar los 12 meses
    const result = [];

    for (let m = 1; m <= 12; m++) {
      const data = map[m] || { mes: m, ventas: 0, compras: 0 };

      result.push({
        ...data,
        flujo: (data.ventas || 0) - (data.compras || 0),
      });
    }

    return result;
  })();



  // 🔥 proyecion tendencia real ventas
  const proyeccionVentas = getProyeccion(ventasTrend);

  // 🔥 alerta real ventas
  const alertaVentas = getVentasAlert(ventasTrend);

  const alertaFlujo =
    enrichedData.slice(-1)[0]?.flujo < 0
      ? {
          tipo: "error",
          titulo: "Flujo negativo",
          descripcion: "Tus compras superan tus ventas este mes",
        }
      : null;

  return (
    <Box sx={{ p: 2 }}>

      <Typography variant="h5" fontWeight={600}>
        Bienvenido, {user.nombre}
      </Typography>

      {/* 🔥 KPI PRINCIPAL CEO */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Typography sx={{ fontSize: 13, color: ui.textSecondary }}>
          Resultado del mes
        </Typography>

        <Typography
          sx={{
            fontSize: 28,
            fontWeight: 700,
            color: kpis.flujoActual >= 0
              ? ui.status.success.bg
              : ui.status.error.text,
          }}
        >
          ${formatMoneyShort(kpis.flujoActual)}
        </Typography>

        <Typography sx={{ fontSize: 12, color: ui.textSecondary }}>
          Ventas - Compras
        </Typography>
      </Card>


    <Card
      sx={{
        p: 2,
        mb: 2,
        background: ui.glass?.background || ui.surfaceBg,
        border: `1px solid ${ui.glass?.border || ui.borderSubtle}`,
        borderLeft: `3px solid ${
          kpis?.flujoActual >= 0
            ? ui.status.success.bg
            : ui.status.error.text
        }`,
        borderRadius: 3,
      }}
    >
      <Typography fontWeight={600}>
        {data?.insight}
      </Typography>
    </Card>


      {/* KPI */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2 }}>

        <KpiCard
          title="Ventas"
          value={kpis?.ventas}
          variation={kpis?.variacionVentas}
          color={ui.primary}
          trend={ventasTrend}
          extra={kpis?.ventasYoY}
        />

        <KpiCard
          title="Compras"
          value={kpis?.compras}
          variation={kpis?.variacionCompras}
          color={ui.secondary}
          trend={comprasTrend}
          extra={kpis?.comprasYoY}
        />

        <KpiCard
          title={`Pendientes (${resumenCesiones?.pendiente?.cantidad || 0})`}
          value={kpis?.cesionesPendientes || 0}
          color={ui.warning}
        />

        <KpiCard
          title={`En nómina (${resumenCesiones?.nomina?.cantidad || 0})`}
          value={kpis?.cesionesNomina || 0}
          color={ui.info}
        />

        <KpiCard
          title={`Pagadas (${resumenCesiones?.pagada?.cantidad || 0})`}
          value={kpis?.cesionesPagadas || 0}
          color={ui.error}
        />


      </Box>

      {/* Salud Financiera */}
      <HealthCard
        kpis={kpis}
        trend={ventasTrend}
        data={enrichedData}
      />

      <Card
        sx={{
          mt: 2,
          p: 2,
          background: ui.glass?.background || ui.surfaceBg,
          border: `1px solid ${ui.glass?.border || ui.borderSubtle}`,
          backdropFilter: "blur(12px)",
        }}
      >
        <Typography sx={{ fontSize: 13, color: ui.textSecondary }}>
          Proyección de cierre mensual
        </Typography>

        <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
          ${formatMoneyShort(proyeccionVentas)}
        </Typography>

        <Typography sx={{ fontSize: 12, color: ui.textSecondary }}>
          Basado en ritmo actual de ventas
        </Typography>
      </Card>

      {/* ACCESOS */}
      <Card
        sx={{
          mt: 2,
          p: 2,
          background: ui.glass?.background || ui.cardBackground,
          border: `1px solid ${ui.glass?.border || ui.borderSubtle}`,
          backdropFilter: "blur(12px)",
        }}
      >
        <Typography sx={{ fontSize: 13, mb: 1.5 }}>
          Accesos rápidos
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2 }}>
          {[
            { label: "Libro de Ventas", route: "/libroventascli" },
            { label: "Libro de Compras", route: "/librocomprascli" },
            { label: "Cesiones", route: "/cesionesdeu" },
            { label: "En Nómina de Pagos", route: "/compagos" },
            { label: "Rentas F22", route: "/rentascli" },
            { label: "Declaraciones Juradas", route: "/djcli" },
          ].map((item) => (
            <Box
              key={item.route} // ✅ FIX React
              onClick={() =>
                navigate(item.route, {
                  state: { from: "dashboard" }, // ✅ breadcrumb inteligente
                })
              }
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                fontSize: 12,
                cursor: "pointer",
                userSelect: "none",

                background: ui.glass.backgroundSoft,
                border: `1px solid ${ui.glass.border}`,

                transition: "all 0.15s ease",

                "&:hover": {
                  background: ui.glass.hover,
                  transform: "translateY(-2px)",
                },

                "&:active": {
                  transform: "translateY(0px) scale(0.98)", // 💡 feedback táctil
                },
              }}
            >
              {item.label}
            </Box>
          ))}
        </Box>
      </Card>

      {/* GRÁFICOS */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2 }}>

        <ChartCard
          title="Ventas vs Compras (Año actual)"
          data={graficos?.ventasVsCompras || []}
          lines={[
            { key: "ventas", color: ui.primary },
            { key: "compras", color: ui.secondary },
          ]}
        />

        <ChartCard
          title="Ventas vs Compras (Año anterior)"
          data={enrichedDataAnterior}
          lines={[
            { key: "ventas", color: ui.primary },
            { key: "compras", color: ui.secondary },
          ]}
        />

      </Box>

      <Box sx={{ mt: 2 }}>
        <ChartCard
          title="Flujo de Caja (Año actual)"
          data={enrichedData}
          lines={[
            { key: "flujo", color: ui.success },
          ]}
        />
      </Box>

      {/* ALERTAS */}
      <Box sx={{ mt: 2 }}>
        {[
          ...(alertas || []).map((a) => {
            if (a.titulo === "Cesiones pendientes") {
              return {
                ...a,
                descripcion: `Tienes ${
                  resumenCesiones?.pendiente?.cantidad || 0
                } cesiones pendientes`,
              };
            }
            return a;
          }),

          ...(alertaVentas ? [alertaVentas] : []),
          ...(alertaFlujo ? [alertaFlujo] : []),
        ].map((a, i) => (
          <AlertCard key={i} alert={a} />
        ))}
      </Box>

    </Box>
  );
};

export default Dashboard;
